import Image from '@tiptap/extension-image';
import { ResizableNodeView } from '@tiptap/core';

/**
 * Email-safe alignment styles applied to the <img> itself (used by renderHTML,
 * so they survive into the sent email and the preview). `display:block` +
 * `margin:auto` is the most reliable horizontal centering across Gmail
 * (web/app) and Apple/iOS Mail. (Outlook desktop ignores margin:auto.)
 */
function imgAlignStyle(align) {
  if (align === 'center') return 'display:block;margin:8px auto;';
  if (align === 'right') return 'display:block;margin:8px 0 8px auto;';
  if (align === 'left') return 'display:block;margin:8px auto 8px 0;';
  return '';
}

/**
 * In the editor, the built-in ResizableNodeView wraps the image in a flex
 * container, so alignment is expressed as the container's justify-content
 * (margin:auto on the img has no effect inside the shrink-wrapped wrapper).
 */
const ALIGN_TO_JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };

/**
 * Extends the standard Image node with:
 *  - an `align` attribute (left/center/right) rendered as email-safe inline
 *    styles on the <img> and round-tripped via data-align, and
 *  - drag-to-resize handles via the official ResizableNodeView (width/height
 *    attributes), kept aspect-locked and responsive (max-width:100%).
 *
 * The node name stays `image`, so setImage, isActive('image'),
 * updateAttributes('image', ...) and the existing paste/drag/upload handlers
 * continue to work unchanged.
 */
export const AlignableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        // null = unaligned (renders like a legacy image)
        default: null,
        parseHTML: (el) => el.getAttribute('data-align') || null,
        renderHTML: (attrs) => {
          const style = imgAlignStyle(attrs.align);
          return attrs.align && style ? { 'data-align': attrs.align, style } : {};
        },
      },
    };
  },

  addNodeView() {
    const resize = this.options.resize;
    if (!resize || !resize.enabled || typeof document === 'undefined') {
      return null;
    }
    const { directions, minWidth, minHeight, alwaysPreserveAspectRatio } = resize;
    const typeName = this.name;

    return ({ node, getPos, HTMLAttributes, editor }) => {
      const el = document.createElement('img');
      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        if (value == null) return;
        if (key === 'width' || key === 'height') return;
        el.setAttribute(key, value);
      });
      el.src = HTMLAttributes.src;

      let containerEl = null;
      const applyAlign = (align) => {
        if (!containerEl) return;
        containerEl.style.justifyContent = ALIGN_TO_JUSTIFY[align] || 'flex-start';
        containerEl.style.margin = '8px 0';
      };

      const nodeView = new ResizableNodeView({
        element: el,
        editor,
        node,
        getPos,
        onResize: (width, height) => {
          el.style.width = `${width}px`;
          el.style.height = `${height}px`;
        },
        onCommit: (width, height) => {
          const pos = getPos();
          if (pos === undefined) return;
          editor.chain().setNodeSelection(pos).updateAttributes(typeName, { width, height }).run();
        },
        onUpdate: (updatedNode) => {
          if (updatedNode.type !== node.type) return false;
          applyAlign(updatedNode.attrs.align);
          return true;
        },
        options: {
          directions,
          min: { width: minWidth, height: minHeight },
          preserveAspectRatio: alwaysPreserveAspectRatio === true,
        },
      });

      containerEl = nodeView.dom;
      applyAlign(node.attrs.align);

      // Avoid a layout flash before the image has loaded (mirrors the library).
      const dom = nodeView.dom;
      dom.style.visibility = 'hidden';
      dom.style.pointerEvents = 'none';
      const reveal = () => {
        dom.style.visibility = '';
        dom.style.pointerEvents = '';
      };
      el.onload = reveal;
      el.onerror = reveal;

      return nodeView;
    };
  },
});

export default AlignableImage;
